/**
 * @captiva/shared-types
 *
 * Unified TypeScript interfaces for the Captiva platform.
 * This is the single source of truth — all apps and services
 * should import domain types from here.
 *
 * When adding new fields, prefer making them optional (?)
 * so that existing consumers are not broken.
 */

export type Timestamp = any;
export type GeoPoint = any;
export type DocumentReference = any;

// ─── Core Domain ──────────────────────────────────────────────────────────────

export enum FuelTemplateNames {
  CAPTURED_TANK_LEVEL_READING = "capturedTankLevelReading",
  CAPTURED_FUEL_TANK_TRANSFER = "capturedFuelTankTransfer",
  CAPTURED_REFUEL = "capturedRefuel",
  CAPTURED_FUEL_TANK_ISSUE = "capturedFuelTankIssue",
  CAPTURED_BULK_FUEL_RECEIVED = "capturedBulkFuelReceived",
}

export interface Company {
  id?: string;
  companyId?: string;
  street: string;
  province: string;
  postalCode: string;
  name: string;
  city: string;
  activationCode?: string;
  phoneNumber?: string;
  addresLine1?: string;
  addresLine2?: string;
  country?: string;
  owner?: string;
  logoUrl?: string;
  fuelUnitOfMeasure?: FuelUnitOfMeasure;
  selectedTemplates?: {
    captivaFuel: FuelTemplateNames[];
    captivaFlex?: string[];
    flexForms?: string[];
    captivaTrip?: TripTemplateTypes[];
  };
  /** WhatsApp Business integration settings for this company */
  whatsappConfig?: WhatsAppCompanyConfig;
  inAppValidationConfig?: {
    captivaFuel?: {
      [template: string]:
      | {
        [field: string]: boolean;
      }
      | undefined;
    };
  };
  metadata?: any;
}

export interface User {
  id: string; // refers to documentID in /users
  emailVerified: boolean;
  firstName: string;
  isActive: boolean;
  lastName: string;
  nextOnboardingStep: string;
  uid: string; // refers to auth UID on auth object
  companyId?: string;
  fleetOperatorId?: string;
  admin?: boolean;
  email?: string;
  displayName?: string;
  selectedTemplates?: {
    captivaFuel?: FuelTemplateNames[];
    captivaFlex?: string[];
    flexForms?: string[];
    captivaTrip?: TripTemplateTypes[];
  };
  metadata?: any;
}

// ─── Fleet & Vehicle ──────────────────────────────────────────────────────────

/**
 * In the codebase this is often called "Fleet" — it represents a single vehicle.
 * Aliased as Vehicle for clarity.
 */
export interface Vehicle {
  id: string;
  companyId: string;
  description: string;
  fleetGroupId: string;
  fuelTypeId?: string;
  isActive: boolean;
  measurementUnit: string;
  fuelCapacity?: number;
  expectedConsumptionRate?: number;
  name: string;
  registrationNumber: string;
  useForRebateClaim: boolean;
  lastOdometer: number;
  lastOdometerDate: string;
  metadata?: any;
}

/** Legacy alias */
export type Fleet = Vehicle;

export interface VehicleGroup {
  id: string;
  companyId: string;
  description: string;
  name: string;
  metadata?: any;
}

/** Legacy alias */
export type FleetGroup = VehicleGroup;

export interface FleetOperator {
  id: string;
  companyId: string;
  employeeCode?: string;
  fullName: string;
  identification?: string;
  nickName?: string;
  metadata?: any;
}

// ─── Assets ───────────────────────────────────────────────────────────────────

export interface Activity {
  id: string;
  companyId: string;
  rebateEligible: boolean;
  name: string;
  metadata?: any;
}

export interface Supplier {
  id: string;
  companyId: string;
  isActive: boolean;
  name: string;
  metadata?: any;
}

export interface Zone {
  id: string;
  companyId: string;
  zoneName: string;
  isActive: boolean;
  sections: Section[];
  metadata?: any;
}

export interface Section {
  areaSizeHectares: number;
  id: string;
  isActive: boolean;
  sectionName: string;
  metadata?: any;
}

export interface Tank {
  id: string;
  companyId: string;
  fuelReceiveBulkDelivery: boolean;
  fuelType: string;
  fuelTypeId?: string;
  isActive: boolean;
  mobileTank: boolean;
  name: string;
  size: string;
  lastDipReading?: number | string;
  lastDipReadingDate?: string;
  metadata?: any;
}

export interface Product {
  id: string;
  companyId: string;
  isActive: boolean;
  name: string;
  metadata?: any;
}

export interface FuelType {
  id: string;
  companyId: string;
  name: string;
  isActive?: boolean;
  metadata?: any;
}

// ─── Trips ────────────────────────────────────────────────────────────────────

export interface Trip {
  id: string | null;
  status: TripStatus;
  requiresPreInspection: boolean;
  locationSnapshots: any[];
  companyId: string;
  capturedTripPreInspection: Array<string>;
  capturedTripDepart: Array<string>;
  capturedTripCheckPoint: Array<string>;
  capturedTripDelay: Array<string>;
  capturedTripArrive: Array<string>;
  capturedTripComplete: Array<string>;
  capturedFuelTankIssue?: Array<string>;
  capturedRefuel?: Array<string>;
  isTrackingActive: boolean;
  tripToken: string;
  pickupAddress: string;
  deliveryAddress: string;
  pickupDate: string;
  deliveryDate: string;
  description: string;
  creatorUid: string;
  creatorEmail: string;
  externalReference?: string;
  notes?: string;
  fleetId?: string;
  isOutOfSync?: boolean;
  notificationEmailAddresses?: string[];
  fleetDescription?: string;
  fleetDisplayName?: string;
  fleetRegistration?: string;
  fleetOdoOrHourBased?: string;
  fleetUseFleetForRebateClaim?: boolean;
  fleetGroupId?: string;
  fleetGroupName?: string;
  fleetOperatorId?: string;
  fleetOperatorEmployeeCode?: string;
  fleetOperatorIdentification?: string;
  fleetOperatorNameAndSurname?: string;
  autoCheckpoints?: AutoCheckpointGeoFence[];
  tripOwnerUid?: string;
  emailNotificationThreadId?: string;
  logoUrl?: string;
  /** E.164 phone numbers that receive WhatsApp notifications for this trip */
  notificationWhatsappNumbers?: string[];
  // populated data arrays (used by trip-viewer)
  capturedTripPreInspectionData?: Array<any>;
  capturedTripDepartData?: Array<any>;
  capturedTripCheckPointData?: Array<any>;
  capturedTripDelayData?: Array<any>;
  capturedTripArriveData?: Array<any>;
  capturedTripCompleteData?: Array<any>;
  // Legacy / service-specific fields
  creatorId?: string;
  creatorName?: string;
  driverId?: string;
  pickupCoordinates?: { latitude: number; longitude: number };
  deliveryCoordinates?: { latitude: number; longitude: number };
}

export interface AutoCheckpointGeoFence {
  identifier: string;
  latitude: number;
  longitude: number;
  radius: number;
  notifyOnEntry: boolean;
  notifyOnExit: boolean;
  extras?: {
    note?: string;
    [key: string]: any;
  };
}

export interface TripTemplateReference {
  name: string;
  timestamp: Timestamp;
  gpsLocation: any[];
  geoPoint: GeoPoint;
  reference: DocumentReference;
}

export interface ExpoLocationSnapshot {
  lon?: number;
  lat?: number;
  timestamp: number;
  speed?: number;
  altitude?: number;
  heading?: number;
  isMoving?: boolean;
  activity?: string;
  coords?: {
    altitude: number;
    heading: number;
    latitude: number;
    altitudeAccuracy: number;
    accuracy: number;
    speed: number;
    longitude: number;
  };
}

// ─── Capture Events ───────────────────────────────────────────────────────────

export interface CapturedTemplate {
  localResourceId?: string;
  template: FuelTemplateTypes | TripTemplateTypes | string;
  metadata?: any;
  data: {
    id?: string;
    companyId: string;
    createdBy: string; // firstName + " " + lastName
    deviceCaptured: string; // ISO 8601
    gpsLocation: any[];
    transactionDate: string; // ISO 8601
    tripId?: string;
    [key: string]: any;
  };
}

/**
 * Granular "Captured*" interfaces referenced in the plan.
 * Currently the codebase represents these via the generic CapturedTemplate
 * with a discriminating `template` field. These aliases are provided for
 * future type-narrowing and documentation purposes.
 */
export type CapturedTankReading = CapturedTemplate;
export type CapturedOnsiteRefuel = CapturedTemplate;
export type CapturedRoadsideRefuel = CapturedTemplate;
export type CapturedBulkFuelDelivery = CapturedTemplate;
export type CapturedTankToTankTransfer = CapturedTemplate;
export type CapturedPreTripInspection = CapturedTemplate;
export type CapturedTripStart = CapturedTemplate;
export type CapturedTripDelay = CapturedTemplate;
export type CapturedTripCheckpoint = CapturedTemplate;
export type CapturedTripArrive = CapturedTemplate;
export type CapturedTripComplete = CapturedTemplate;

// ─── Credits & Billing ────────────────────────────────────────────────────────

export interface CreditTransaction {
  date?: any;
  userId: string;
  action: string | CreditAction;
  creditQuantity: number;
  reference?: string;
}

export interface CompanyCredits {
  companyId: string;
  creditBalance: number;
  averageDailyConsumption: number;
  isEnterprisePriced?: boolean;
  unprocessedTransactionCount: number;
  unprocessedTransactions: CreditTransaction[];
  processedTransactions: CreditTransaction[];
  transactionArchive?: {
    archiveId: string;
    processedTransactions: CreditTransaction[];
  }[];
  /** Invoice references stored on the credit document (from firebase.payments) */
  invoices?: CreditInvoiceReference[];
}

/**
 * A lightweight reference stored inside the CompanyCredits.invoices array.
 * Created by the payments service when a RevenueCat purchase is processed.
 */
export interface CreditInvoiceReference {
  invoiceNumber: string;
  orderId: string;
  date: Date | Timestamp;
}

/**
 * CreditLog — represents an entry in processedTransactions.
 * Identical shape to CreditTransaction; aliased for semantic clarity.
 */
export type CreditLog = CreditTransaction;

/**
 * CreditPurchaseInvoice — a completed order stored in
 * Firestore: companies/{companyId}/completedOrders
 * Shape extracted from firebase.payments/functions/index.ts
 */
export interface CreditPurchaseInvoice {
  id?: string;
  companyId: string;
  credits: string;
  currency: string;
  date: Date | Timestamp;
  email: string;
  invoiceNumber: string;
  money: number;
  name: string;
  city: string;
  postalCode: string;
  province: string;
  street: string;
  tax: number;
  taxNumber: string;
  uid: string;
}

// ─── Notification & Report Configs ────────────────────────────────────────────

/**
 * Shape inferred from report-viewer notification-configuration.js
 * Firestore collection: reportNotifications
 */
export interface FuelNotificationConfig {
  companyId: string;
  emails: string[];
  hasDaily: boolean;
  hasWeekly: boolean;
  hasMonthly: boolean;
  daily: string[]; // report IDs
  weekly: string[];
  monthly: string[];
  onCaptured?: string[];
  /** E.164 numbers for WhatsApp scheduled and on-capture report notifications */
  whatsappNumbers?: string[];
  anomalyConfig?: AnomalyDetectionConfig;
}

/**
 * Shape inferred from report-viewer flex-notification-configuration.js
 * Firestore collection: flexReportNotifications
 */
export interface FlexNotificationConfig {
  companyId: string;
  emails: string[];
  hasDaily: boolean;
  hasWeekly: boolean;
  hasMonthly: boolean;
  daily: string[];
  weekly: string[];
  monthly: string[];
  /** E.164 numbers for WhatsApp scheduled report notifications */
  whatsappNumbers?: string[];
  anomalyConfig?: AnomalyDetectionConfig;
}

export interface TemplateTimeframeAdjustment {
  useFullHistory?: boolean;
  startMinus?: number; // days to subtract from startDate
  endPlus?: number; // days to add to endDate
}

/**
 * Firestore collection: reportSetUp
 */
export interface FuelReportConfig {
  dataSource?: "bigquery" | "firestore";
  includedTemplates?: string[];
  reportTemplateLink: string;
  renderingType: string;
  isAdvancedReport: boolean;
  reportShortName: string;
  moduleName?: string;
  /**
   * @deprecated AlaSQL flow is deprecated. Use BigQuery configs instead.
   */
  reportSelectStatement?: string;
  reportDateFieldName?: string;
  reportDescription?: string;
  createdOn?: Timestamp;
  isDateRanged?: boolean;
  reportCategory?: string;
  createdByUser?: string;
  isActive?: boolean;
  viewName?: string;
  reportCompanyFieldName?: string;
  additionalFilters?: ReportFilter[];
  reportConstants?: Record<string, any>;
  /**
   * @deprecated Hardcoded static steps are legacy. Dynamic or BigQuery flows are preferred, but this remains for legacy support.
   */
  postProcessingType?: string[];
  postProcessingParams?: Record<string, any>;
  includedTemplatesTimeframeAdjustments?: TemplateTimeframeAdjustment[];
  // Dynamic logic (phase 1+): optional external scripts hosted in GCS and executed in vm sandbox
  enableDynamicExecution?: boolean;
  /**
   * @deprecated AlaSQL flow is deprecated. Use BigQuery configs instead.
   */
  dynamicAlaSqlFunctions?: DynamicAlaSqlFunctionRegistration[];
  dynamicPostProcessing?: DynamicPostProcessingStep[];
  dynamicExecutionPolicy?: DynamicScriptExecutionPolicy;
  runtimeFilters?: RuntimeFilterDefinition[];
  id?: string;
  name?: string;
}

export interface RuntimeFilterDefinition {
  id: string;                                // Parameter variable name (e.g. 'filterStoreroom', 'filterBlock')
  label: string;                             // UI Label displayed above input
  type: "dropdown" | "number" | "text" | "toggle"; // Input control type
  defaultValue: any;                         // Initial default value (e.g. 'ALL', 14, 0)
  placeholder?: string;                      // Placeholder text
  options?: string[];                        // Static options list (optional)
  optionsSource?: string;                    // Dynamic OptionSet identifier (e.g. 'optionSet:storerooms')
  min?: number;                              // Min value (for number inputs)
  max?: number;                              // Max value (for number inputs)
  step?: number;                             // Increment step (for number inputs)
  required?: boolean;                        // Is input mandatory (default false)
}

export interface ReportFilter {
  field: string;
  operator: "==" | ">" | "<" | ">=" | "<=" | "in" | "array-contains";
  value: any;
}

/**
 * Dynamic Script Metadata and Execution Policy (shared across services)
 * Imported from firebase.reports service contracts, re-exported for admin tooling
 */
export interface DynamicScriptMetadata {
  version: string;
  sha256: string;
  publishedAt: string;
  author: string;
  releaseNotes?: string;
}

export interface DynamicScriptExecutionPolicy {
  timeoutMs: number;
  failClosed: boolean;
  fallbackStaticFunction?: string;
  requiresFeatureFlag?: string;
}

export interface DynamicPostProcessingStep {
  type: "dynamic";
  scriptPath: string;
  metadata: DynamicScriptMetadata;
  executionPolicy: DynamicScriptExecutionPolicy;
}

export interface DynamicAlaSqlFunctionRegistration {
  type: "dynamic";
  scriptPath: string;
  metadata: DynamicScriptMetadata;
  executionPolicy: DynamicScriptExecutionPolicy;
}

/**
 * Firestore collection: flexReports
 * Currently shares the same shape as FuelReportConfig;
 * aliased separately in case the shape diverges.
 */
export type FlexReportConfig = FuelReportConfig;
export type ReportConfig = FuelReportConfig;

/**
 * Documents in the `mail` Firestore collection, consumed by the
 * Firebase Trigger Email extension.
 * Shape confirmed across firebase.payments sendErrorEmail,
 * firebase.app-api company create, and firebase.credits low-balance warning.
 */
export interface EmailNotification {
  to: string[];
  bcc?: string[];
  message: {
    subject: string;
    html: string;
  };
}

// ─── Flex Form System ─────────────────────────────────────────────────────────

export enum FlexFormInputType {
  TEXT = "text",
  NUMBER = "number",
  DATE = "date",
  DATETIME = "datetime",
  TIME = "time",
  BOOLEAN = "boolean",
  SELECT = "select",
  MULTI_SELECT = "multiSelect",
  SIGNATURE = "signature",
  PHOTO = "photo",
  MULTI_PHOTO = "multiPhoto",
  BARCODE = "barcode",
  MULTI_BARCODE = "multiBarcode",
  CHECKLIST = "checklist",
  CHECKLIST_WITH_NOTES = "checklistWithNotes",
  GPS = "gps",
  NFC = "nfc",
}

export interface FormFieldValidation {
  required?: boolean;
  expectedValue?: any;
  range?: { min: number; max: number };
  regex?: string;
}

export interface FlexFormSelectOption {
  label: string;
  value: any;
  metadata?: Record<string, OptionsMetadata>;
}

export interface FlexFormFirestoreSelectOptionSet {
  id: string;
  name?: string;
  label?: string;
  options: FlexFormSelectOption[];
  metadata?: Record<string, any>;
  linkedFormTemplateId?: string;
  mappingConfig?: any;
  autoSync?: boolean;
  lastSyncedAt?: any;
  updatedAt?: any;
  moduleId?: string | null;
  companyId?: string;
  moduleMetadata?: any;
}

/** The user-facing name from the plan. Alias of FlexFormFirestoreSelectOptionSet. */
export type FlexOptionsSet = FlexFormFirestoreSelectOptionSet;

export type ChecklistNoteInputType = "text" | "number";

export interface FlexFormChecklistItemValue {
  id: string;
  label: string;
  checked: boolean;
  note?: string | number | null;
  metadata?: Record<string, OptionsMetadata>;
}

export interface FormFieldCondition {
  fieldId: string;
  operator: "equals" | "notEquals" | "greaterThan" | "lessThan" | "in" | "notIn";
  value: any;
}

export interface FormFieldDependency {
  fieldId: string;
  action: "reset" | "update";
  value?: any;
}

export interface OptionsMetadata {
  label: string;
  value: any;
}

export enum OptionsSourceType {
  STATIC = "static",
  FIRESTORE = "firestore",
}

export type OptionsSource =
  | { type: "static"; options: Array<FlexFormSelectOption> }
  | { type: OptionsSourceType.FIRESTORE | string; collection?: string; document?: string; id?: string; optionsSetId?: string; options?: any[] };

export interface FormFieldConfig {
  id: string;
  key: string;
  label: string;
  type: FlexFormInputType;
  validation: FormFieldValidation | null;
  hidden?: boolean | null;
  readOnly?: boolean | null;
  displayIf?: FormFieldCondition[] | null;
  optionsSource?: OptionsSource | null;
  checklistNoteInputType?: ChecklistNoteInputType | null;
  defaultValue?: any;
  includeInPdf?: boolean | null;
  includeMetadataInPdf?: boolean | null;
  canPrepopulate?: boolean | null;
  dependencies?: FormFieldDependency[] | null;
}

export interface FormSectionConfig {
  id: string;
  title: string;
  fields: string[];
}

export interface FormLevelValidation {
  expectedValues: Record<string, any> | null;
  ranges: Record<string, { min: number; max: number }> | null;
  regexes: Record<string, string> | null;
  message: string;
}

export interface CompanyAssets {
  companyId: string;
  vehicles: unknown[];
  tanks: unknown[];
  operators: unknown[];
  flexFormTemplates: unknown[];
  flexFormSelectOptions: unknown[];
  templates: unknown[];
  activities: unknown[];
  products: unknown[];
  suppliers: unknown[];
  areaForFleetUse: unknown[];
}

export interface CompanyReports {
  companyId: string;
  notificationConfig: Record<string, unknown> | null;
  reportSetUp: unknown[];
}

export interface FlexFormTemplate {
  id?: string;
  companyId?: string;
  name: string;
  code?: string | null;
  status?: "draft" | "published" | "archived" | string | null;
  version?: number | null;
  sourceModuleId?: string | null;
  draftClaimToken?: string | null;
  description?: string | null;
  active?: boolean | null;
  prefix?: string | null;
  notificationSubjectLine?: string | null;
  notificationBody?: string | null;
  notificationEmails?: string[] | null;
  notificationWhatsappNumbers?: string[] | null;
  hideNullishInPdf?: boolean | null;
  pdfLogoMaxWidth?: number | null;
  pdfPhotoMaxWidth?: number | null;
  pdfSignatureMaxWidth?: number | null;
  moduleId?: string | null;
  archived?: boolean | null;
  moduleMetadata?: any;
  fields: FormFieldConfig[];
  sections?: FormSectionConfig[] | null;
  formLevelValidations?: FormLevelValidation[] | null;
  isPublicCaptureEnabled?: boolean | null;
  requireAuthentication?: boolean | null;
  guestSubmitterLabel?: string | null;
  updatedAt?: Timestamp;
  anomalyConfig?: AnomalyDetectionConfig | null;
}

/**
 * FlexAIStoryTemplate — stored in Firestore: flexFormStoryTemplates/{templateId}
 * Shape inferred from report-viewer useStoryTemplate.js hook
 * and the tenantapi PUT /flexFormStoryTemplates/:id endpoint.
 */
export interface FlexAIStoryTemplate {
  id?: string;
  templateId: string;
  companyId: string;
  /** Handlebars template string used for AI story generation */
  handlebarsTemplate: string;
  /** Whether this story template is actively used */
  active: boolean;
}

export interface TemplateSubmissionField {
  type: FlexFormInputType;
  value: any;
  label?: string;
  metadata?: any;
}

export interface TemplateSubmission {
  localResourceId: string;
  deviceCaptured: string;
  templateId: string;
  templateName: string;
  companyId: string;
  submitterName: string;
  submitterUid: string;
  submitterEmail: string;
  data: Record<string, TemplateSubmissionField>;
  /** Populated by Layer 3 Contextual Anomaly Judge when an anomaly is evaluated */
  anomalySummary?: AnomalySummary;
}

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum TripStatus {
  PLANNED = "PLANNED",
  ALLOCATED = "ALLOCATED",
  PRE_INSPECTED = "PRE_INSPECTED",
  IN_PROGRESS = "IN_PROGRESS",
  ARRIVED = "ARRIVED",
  COMPLETED = "COMPLETED",
}

export enum TripTemplateTypes {
  capturedTripPreInspection = "capturedTripPreInspection",
  capturedTripDepart = "capturedTripDepart",
  capturedTripCheckPoint = "capturedTripCheckPoint",
  capturedTripDelay = "capturedTripDelay",
  capturedTripArrive = "capturedTripArrive",
  capturedTripComplete = "capturedTripComplete",
  capturedRefuel = "capturedRefuel",
  capturedFuelTankIssue = "capturedFuelTankIssue",
}

export type FuelTemplateTypes =
  | "capturedTankLevelReading"
  | "capturedRefuel"
  | "capturedFuelTankTransfer"
  | "capturedFuelTankIssue"
  | "capturedBulkFuelReceived";

export enum FuelUnitOfMeasure {
  LITRES = "LITRES",
  GALLONS = "GALLONS",
}

export enum CreditAction {
  SAVE_TEMPLATE = "SAVE_TEMPLATE",
  UPDATE_TEMPLATE = "UPDATE_TEMPLATE",
  SAVE_MASTER_DATA = "SAVE_MASTER_DATA",
  UPDATE_MASTER_DATA = "UPDATE_MASTER_DATA",
  GENERATE_REPORT = "GENERATE_REPORT",
  REPLENISH = "REPLENISH",
  INTER_COMPANY_TRANSFER = "INTER_COMPANY_TRANSFER",
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const FleetUnitOptions = [
  { key: "KILOMETERS", value: "KILOMETERS" },
  { key: "HOURS", value: "HOURS" },
  { key: "MILES", value: "MILES" },
  { key: "NONE", value: "NONE" },
] as const;

export interface CompanySummary {
  companyId: string;
  name: string;
  city: string;
  owner: string;
  ownerEmail?: string;
  phoneNumber?: string;
  creditBalance?: number;
  lastInteraction?: string;
}

export interface CompanyUser {
  uid: string;
  email: string;
  displayName: string;
  admin: boolean;
  companyId: string;
}

// ─── WhatsApp Integration ──────────────────────────────────────────────────────

/**
 * Stored at: whatsappCompanyConfig/{companyId}
 * Controls per-company WhatsApp notification delivery.
 */
export interface WhatsAppCompanyConfig {
  /** Company/tenant identifier (matches document ID) */
  companyId: string;
  /** Master toggle — false disables all outbound WhatsApp for this company */
  enabled: boolean;
  /** Meta WABA phone number ID (from WhatsApp Manager > Account tools > Phone numbers) */
  phoneNumberId: string;
  /** WhatsApp Business Account ID */
  wabaId: string;
  /** BCP-47 language code for template language selection, e.g. "en", "af" */
  defaultLanguageCode: string;
  /**
   * When true the sender logs outbound payloads but does NOT call the Meta API.
   * Use this for staging environments to avoid consuming Meta quota.
   */
  dryRun?: boolean;
  /**
   * Allowlist of flows that may send WhatsApp messages for this company.
   * Omit to allow all flows when the master `enabled` flag is true.
   */
  enabledFlows?: WhatsAppNotificationFlow[];
}

/**
 * All distinct Captiva notification event flows that can be delivered via WhatsApp.
 */
export type WhatsAppNotificationFlow =
  | "trip_departed"
  | "trip_arrived"
  | "trip_checkpoint"
  | "trip_delay"
  | "trip_pre_inspection"
  | "trip_completed"
  | "fuel_delivery"
  | "fuel_issue"
  | "fuel_transfer"
  | "fuel_level_reading"
  | "fuel_refuel"
  | "scheduled_report"
  | "flex_submission"
  | "anomaly_alert";

export type WhatsAppFlow = WhatsAppNotificationFlow;

/**
 * Stored at: whatsappUserIdentities/{companyId}_{phone}
 * Links a verified WhatsApp phone number to a Firebase auth user.
 * Semi-strict policy: system recipients (company-level) do not require a user link.
 */
export interface WhatsAppUserIdentity {
  /** E.164 phone number */
  phone: string;
  /** Firebase auth UID */
  uid: string;
  companyId: string;
  verified: boolean;
  verifiedAt?: Timestamp;
  /** Explicit opt-in to receive WhatsApp notifications */
  optedIn: boolean;
  optedInAt?: Timestamp;
  createdAt?: Timestamp;
}

/**
 * Stored at: whatsappOptOuts/{E164_phone}
 * Global opt-out record — a STOP keyword opts the number out across all companies.
 */
export interface WhatsAppOptOut {
  phone: string;
  optedOutAt: Timestamp;
  source: "user_stop" | "admin";
  companyId?: string;
}

/**
 * Stored at: whatsappDelivery/{companyId}/messages/{idempotencyKey}
 * Tracks the full lifecycle of an outbound WhatsApp message.
 */
export interface WhatsAppDeliveryRecord {
  companyId: string;
  /** Full E.164 phone — stored but not included in logs */
  recipientPhone: string;
  /** Partially masked phone for log-safe display */
  recipientPhoneMasked: string;
  /** Message ID returned by the Meta API after successful send */
  waMessageId?: string;
  templateName: string;
  flow: WhatsAppNotificationFlow;
  sourceEventId: string;
  status: "pending" | "sent" | "delivered" | "read" | "failed";
  sentAt?: Timestamp;
  statusUpdatedAt?: Timestamp;
  errorCode?: string;
  errorMessage?: string;
  /** SHA-256 of (companyId|flow|sourceEventId|phone) for function-retry deduplication */
  idempotencyKey: string;
}

export type FormInputType =
  | "text" // Single-line text input
  | "number" // Numeric input
  | "date" // Date picker
  | "datetime" // Date and time picker
  | "time" // Time picker
  | "boolean" // Checkbox or switch
  | "select" // Dropdown/select
  | "multiSelect" // Multi-select dropdown
  | "signature" // Signature pad
  | "photo" // Single photo input
  | "multiPhoto" // Multiple photo input
  | "barcode" // Single barcode scan
  | "multiBarcode" // Multiple barcode scans
  | "checklist" // Checklist with checkboxes
  | "checklistWithNotes" // Checklist with checkboxes and notes
  | "gps"
  | "nfc";

export interface FlexModuleTemplate {
  id: string; // Unique within module
  name: string; // Template name
  templateData: Omit<FlexFormTemplate, 'companyId'>; // Template without companyId
  storyTemplate?: {
    handlebarsTemplate: string;
    active: boolean;
  };
}

export interface FlexModuleReport {
  reportId: string; // References reportSetUp document ID
  name: string; // Cached for display
  description?: string; // Cached description
  reportData?: any;
}

export interface FlexModuleOptionsSet {
  id: string; // Unique within module
  name: string; // Display name
  placeholderData: FlexFormFirestoreSelectOptionSet; // Template with empty options
  options?: any[];
  description?: string;
  linkedFormTemplateId?: string;
  mappingConfig?: any;
  autoSync?: boolean;
  lastSyncedAt?: any;
}

export interface FlexModule {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  templates: FlexModuleTemplate[];
  reports: FlexModuleReport[];
  optionsSets: FlexModuleOptionsSet[];
  instructions?: string; // Simple text instructions
  videoUrl?: string; // Optional video link
  nextSteps?: string[]; // Ordered list of recommended actions
  sampleOptionsSets?: Array<any>;
  version?: string;
}

export interface AutoVerifiedData {
  verified: boolean;
  error: string;
  verifiedData: Record<string, boolean | undefined | null>;
  reasons?: Record<string, string>;
  source: string;
  verifiedAt: any;
}

/**
 * Flattens select field option metadata into a hybrid String object structure.
 * Supports both flat properties (e.g. {{customer.email}}) and legacy nested properties (e.g. {{customer.email.value}}).
 */
export function flattenSelectFieldMetadata(metadata: any, value: any): any {
  const strValue = value !== undefined && value !== null ? String(value) : "";
  if (!metadata) {
    const emptyMeta: any = { value, label: "" };
    Object.defineProperty(emptyMeta, "toString", {
      value: () => strValue,
      enumerable: false,
      configurable: true,
    });
    return emptyMeta;
  }
  const selectMeta: any = { value, label: metadata.label || "" };
  selectMeta.metadata = {};
  Object.defineProperty(selectMeta, "toString", {
    value: () => strValue,
    enumerable: false,
    configurable: true,
  });

  const combinedEntries: Array<[string, any]> = [];
  if (metadata.metadata && typeof metadata.metadata === "object" && !Array.isArray(metadata.metadata)) {
    combinedEntries.push(...Object.entries(metadata.metadata));
  }
  combinedEntries.push(...Object.entries(metadata));

  for (const [metaKey, metaVal] of combinedEntries) {
    if (metaKey === "metadata" && typeof metaVal === "object" && metaVal !== null && !("value" in metaVal)) {
      continue;
    }
    selectMeta.metadata[metaKey] = metaVal;
    if (metaVal && typeof metaVal === "object" && "value" in metaVal) {
      const metaValStr = new String((metaVal as any).value);
      (metaValStr as any).value = (metaVal as any).value;
      (metaValStr as any).label = (metaVal as any).label || "";
      selectMeta[metaKey] = metaValStr;
    } else if (metaKey !== "value") {
      selectMeta[metaKey] = metaVal;
    }
  }
  return selectMeta;
}

export interface FlattenFlexSubmissionOptions {
  locale?: string;                   // Defaults to 'en-GB'
  timeZone?: string;                 // Defaults to 'Africa/Johannesburg'
  gpsFormat?: 'lng-lat' | 'lat-lng'; // Defaults to 'lng-lat' (GeoJSON/database standard)
  formatDatesAndGPS?: boolean;       // Defaults to true (set false for AI prompts/RAG)
}

/**
 * Flattens a flex submission's field data into a flat key-value map.
 * Formats checklist, select metadata, multi-select, GPS maps, dates, times, and booleans.
 */
export function flattenFlexSubmissionData(
  data: any,
  options: FlattenFlexSubmissionOptions = {}
): Record<string, any> {
  const formatDatesAndGPS = options.formatDatesAndGPS !== false;
  const gpsFormat = options.gpsFormat || 'lng-lat';
  const locale = options.locale || 'en-GB';
  const timeZone = options.timeZone || 'Africa/Johannesburg';

  const flattenedData: Record<string, any> = {};
  if (data.data) {
    for (const [key, field] of Object.entries(data.data)) {
      const fieldData = field as any;
      if (!fieldData) continue;

      if (fieldData.type === 'select' || fieldData.type === 'singleSelect') {
        const valStr = fieldData.value !== undefined && fieldData.value !== null ? String(fieldData.value) : '';
        if (fieldData.metadata) {
          const flattenedMeta = flattenSelectFieldMetadata(fieldData.metadata, fieldData.value);
          if (!flattenedMeta.label) {
            flattenedMeta.label = valStr;
          }
          flattenedData[key] = flattenedMeta;
          flattenedData[`${key}_label`] = fieldData.metadata.label || valStr;
        } else {
          flattenedData[key] = flattenSelectFieldMetadata({ label: valStr }, fieldData.value);
          flattenedData[`${key}_label`] = valStr;
        }
      } else if (fieldData.type === 'multiSelect' || fieldData.type === 'multiselect') {
        const rawArray = Array.isArray(fieldData.value)
          ? fieldData.value
          : fieldData.value !== undefined && fieldData.value !== null && fieldData.value !== ''
          ? [fieldData.value]
          : [];
        const metaArray = Array.isArray(fieldData.metadata) ? fieldData.metadata : null;

        const arr = rawArray.map((rawItem: any, index: number) => {
          const isObjItem = typeof rawItem === 'object' && rawItem !== null;
          const itemVal = isObjItem && 'value' in rawItem ? rawItem.value : rawItem;
          const itemMeta =
            (metaArray && metaArray[index]) ||
            (isObjItem && rawItem.metadata) ||
            (isObjItem ? rawItem : null);

          const flattenedItem = flattenSelectFieldMetadata(
            itemMeta && typeof itemMeta === 'object'
              ? itemMeta
              : { label: String(itemMeta ?? itemVal ?? '') },
            itemVal
          );
          if (!flattenedItem.label && itemVal !== undefined && itemVal !== null) {
            flattenedItem.label = String(itemVal);
          }
          return flattenedItem;
        });

        const joinedValues = arr
          .map((item: any) => (item.value !== undefined && item.value !== null ? String(item.value) : ''))
          .filter(Boolean)
          .join(', ');
        const joinedLabels = arr
          .map((item: any) => String(item.label || item.value || ''))
          .filter(Boolean)
          .join(', ');

        Object.defineProperty(arr, 'toString', {
          value: () => joinedValues,
          enumerable: false,
          configurable: true,
        });

        if (fieldData.metadata && !Array.isArray(fieldData.metadata) && !Array.isArray(fieldData.value)) {
          flattenedData[key] = fieldData.metadata;
        } else {
          flattenedData[key] = arr;
        }
        flattenedData[`${key}_labels`] = joinedLabels;
        flattenedData[`${key}_label`] = joinedLabels;
      } else if (fieldData.type === 'checklist' || fieldData.type === 'checklistWithNotes') {
        const items = Array.isArray(fieldData.value) ? fieldData.value : [];
        const normalizedItems = items.map((item: any) => ({
          ...item,
          value: item?.id,
          ...(item?.metadata || {}),
        }));
        const checkedItems = normalizedItems.filter((item: any) => item?.checked);

        flattenedData[key] = normalizedItems;
        flattenedData[`${key}_checked`] = checkedItems;
        flattenedData[`${key}_checkedIds`] = checkedItems.map((item: any) => String(item.id));
        flattenedData[`${key}_checkedLabels`] = checkedItems.map((item: any) => item.label).filter(Boolean).join(', ');
        flattenedData[`${key}_checkedCount`] = checkedItems.length;
        flattenedData[`${key}_uncheckedCount`] = normalizedItems.length - checkedItems.length;
        if (fieldData.type === 'checklistWithNotes') {
          flattenedData[`${key}_withNotes`] = normalizedItems.filter(
            (item: any) => item.note !== undefined && item.note !== null && item.note !== ''
          );
        }
      } else if (formatDatesAndGPS && fieldData.type === 'date') {
        flattenedData[key] = fieldData.value 
          ? new Date(fieldData.value).toLocaleDateString(locale, { timeZone }) 
          : '';
      } else if (formatDatesAndGPS && fieldData.type === 'time') {
        flattenedData[key] = fieldData.value 
          ? new Date(fieldData.value).toLocaleTimeString(locale, { timeZone }) 
          : '';
      } else if (formatDatesAndGPS && fieldData.type === 'datetime') {
        flattenedData[key] = fieldData.value 
          ? new Date(fieldData.value).toLocaleString(locale, { timeZone }) 
          : '';
      } else if (formatDatesAndGPS && fieldData.type === 'gps') {
        flattenedData[key] = fieldData.value;
        if (Array.isArray(fieldData.value) && fieldData.value.length === 2) {
          const [val1, val2] = fieldData.value;
          const lat = gpsFormat === 'lat-lng' ? val1 : val2;
          const lng = gpsFormat === 'lat-lng' ? val2 : val1;
          flattenedData[`${key}_maplink`] = `https://www.google.com/maps?q=${lat},${lng}`;
        }
      } else if (fieldData.type?.toLowerCase() === 'boolean') {
        flattenedData[key] = fieldData.value === true || fieldData.value === 'true';
      } else {
        flattenedData[key] = fieldData.value;
      }
    }
  }

  // Top level fields (do not clobber explicit form fields in data.data if data.id is undefined)
  if (data.id !== undefined || flattenedData.id === undefined) {
    flattenedData.id = data.id ?? flattenedData.id;
  }
  flattenedData.documentNumber = data.documentNumber;
  flattenedData.templateName = data.templateName;
  flattenedData.submitterName = data.submitterName;
  flattenedData.submitterEmail = data.submitterEmail;

  if (data.submissionDate) {
    if (typeof data.submissionDate.toDate === 'function') {
      flattenedData.submissionDate = data.submissionDate.toDate().toLocaleString(locale, { timeZone });
    } else {
      flattenedData.submissionDate = new Date(data.submissionDate).toLocaleString(locale, { timeZone });
    }
  } else {
    flattenedData.submissionDate = '';
  }

  return flattenedData;
}

/**
 * Lightweight, Hermes-safe template interpolator for Option Set mapping templates (e.g. "{{Name}} {{Surname}}").
 * Supports both double {{path}} and triple {{{path}}} braces as well as dot-separated nested keys.
 */
export function interpolateOptionTemplate(
  templateStr: string | undefined,
  context: Record<string, any>
): string {
  if (!templateStr || typeof templateStr !== 'string') return '';
  try {
    const resolveKey = (rawKey: string): string => {
      const key = rawKey.trim();
      if (!key) return '';
      if (key in context && context[key] !== undefined && context[key] !== null) {
        return String(context[key]);
      }
      const parts = key.split('.');
      let current: any = context;
      for (const part of parts) {
        if (current === undefined || current === null) return '';
        current = current[part];
      }
      if (current === undefined || current === null) return '';
      return String(current);
    };

    return templateStr
      .replace(/\{\{\{([^{}]+)\}\}\}/g, (_, k) => resolveKey(k))
      .replace(/\{\{([^{}]+)\}\}/g, (_, k) => resolveKey(k))
      .trim();
  } catch {
    return '';
  }
}

/**
 * Evaluates a single Flex submission against an Option Set mappingConfig to produce a FlexFormSelectOption.
 * Returns null if either the evaluated label or value is empty.
 */
export function evaluateSubmissionForOptionSet(
  submission: any,
  mappingConfig: any,
  compiler: (templateStr: string | undefined, context: Record<string, any>) => string = interpolateOptionTemplate
): FlexFormSelectOption | null {
  if (!submission || !mappingConfig?.labelTemplate || !mappingConfig?.valueTemplate) {
    return null;
  }
  const flatData = flattenFlexSubmissionData(submission);
  const label = compiler(mappingConfig.labelTemplate, flatData);
  const value = compiler(mappingConfig.valueTemplate, flatData);
  if (!label || !value) {
    return null;
  }

  const metadata: Record<string, OptionsMetadata> = {};
  let hasMetadata = false;
  if (mappingConfig.metadata && typeof mappingConfig.metadata === 'object') {
    for (const [metaKey, metaDef] of Object.entries<any>(mappingConfig.metadata)) {
      if (metaDef && typeof metaDef.template === 'string') {
        metadata[metaKey] = {
          label: metaDef.label || metaKey,
          value: compiler(metaDef.template, flatData),
        };
        hasMetadata = true;
      }
    }
  }

  return {
    label,
    value,
    ...(hasMetadata ? { metadata } : {}),
  };
}

/**
 * Applies a single submission optimistically to an Option Set if the Option Set is linked
 * to the submission's template and autoSync is enabled (autoSync !== false).
 */
export function applySubmissionToOptionSet(
  optionSet: FlexFormFirestoreSelectOptionSet,
  submission: any,
  compiler: (templateStr: string | undefined, context: Record<string, any>) => string = interpolateOptionTemplate
): FlexFormFirestoreSelectOptionSet {
  if (
    !optionSet ||
    !submission ||
    !optionSet.linkedFormTemplateId ||
    optionSet.linkedFormTemplateId !== submission.templateId ||
    optionSet.autoSync === false ||
    !optionSet.mappingConfig
  ) {
    return optionSet;
  }

  const evaluated = evaluateSubmissionForOptionSet(submission, optionSet.mappingConfig, compiler);
  if (!evaluated) {
    return optionSet;
  }

  const existingOptions = Array.isArray(optionSet.options) ? [...optionSet.options] : [];
  const existingIndex = existingOptions.findIndex(
    (opt) => String(opt?.value ?? '') === String(evaluated.value)
  );

  if (existingIndex >= 0) {
    existingOptions[existingIndex] = evaluated;
  } else {
    existingOptions.push(evaluated);
  }

  return {
    ...optionSet,
    options: existingOptions,
  };
}

/**
 * Applies an edited submission to an Option Set in-place, updating the option generated from
 * `previousSubmission` (even when the evaluated `value` itself changed during the edit).
 */
export function applySubmissionEditToOptionSet(
  optionSet: FlexFormFirestoreSelectOptionSet,
  previousSubmission: any,
  updatedSubmission: any,
  compiler: (templateStr: string | undefined, context: Record<string, any>) => string = interpolateOptionTemplate
): FlexFormFirestoreSelectOptionSet {
  const targetTemplateId = updatedSubmission?.templateId || previousSubmission?.templateId;
  if (
    !optionSet ||
    !updatedSubmission ||
    !optionSet.linkedFormTemplateId ||
    optionSet.linkedFormTemplateId !== targetTemplateId ||
    optionSet.autoSync === false ||
    !optionSet.mappingConfig
  ) {
    return optionSet;
  }

  const prevEvaluated = previousSubmission
    ? evaluateSubmissionForOptionSet(previousSubmission, optionSet.mappingConfig, compiler)
    : null;
  const nextEvaluated = evaluateSubmissionForOptionSet(updatedSubmission, optionSet.mappingConfig, compiler);

  if (!nextEvaluated) {
    return optionSet;
  }

  const existingOptions = Array.isArray(optionSet.options) ? [...optionSet.options] : [];
  const matchByNewIdx = existingOptions.findIndex(
    (opt) => String(opt?.value ?? '') === String(nextEvaluated.value)
  );
  const matchByPrevIdx =
    prevEvaluated && String(prevEvaluated.value) !== String(nextEvaluated.value)
      ? existingOptions.findIndex((opt) => String(opt?.value ?? '') === String(prevEvaluated.value))
      : -1;

  if (matchByNewIdx >= 0) {
    existingOptions[matchByNewIdx] = nextEvaluated;
    if (matchByPrevIdx >= 0 && matchByPrevIdx !== matchByNewIdx) {
      existingOptions.splice(matchByPrevIdx, 1);
    }
  } else if (matchByPrevIdx >= 0) {
    existingOptions[matchByPrevIdx] = nextEvaluated;
  } else {
    existingOptions.push(nextEvaluated);
  }

  return {
    ...optionSet,
    options: existingOptions,
  };
}

/**
 * Validates if a phone number is in standard E.164 format (e.g. +27821234567).
 */
export function isValidE164PhoneNumber(phone: string): boolean {
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phone);
}

/**
 * Sanitizes emails array/string by trimming and filtering out falsy values.
 */
export function sanitizeEmails(emails: any): string[] {
  if (!emails) return [];
  const array = Array.isArray(emails) ? emails : [emails];
  return array
    .flat(Infinity)
    .map(e => typeof e === 'string' ? e.trim() : '')
    .filter(Boolean);
}

export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Extracts coordinates from long-form Google Maps & Apple Maps URLs.
 */
export function extractCoordsFromUrl(urlStr: string): Coordinates | null {
  try {
    const url = new URL(urlStr.trim());
    const host = url.hostname.toLowerCase();

    // 1. Loop through all search parameters to find any coordinate-like values
    let foundCoords: Coordinates | null = null;
    url.searchParams.forEach((val) => {
      if (foundCoords) return;
      const match = val.match(/(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/);
      if (match) {
        foundCoords = { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
      }
    });
    if (foundCoords) return foundCoords;

    // 2. Google Maps path check (e.g. pathname includes /@33.828,-84.375)
    if (
      host.includes("google.") &&
      (host.includes("maps") ||
        url.pathname.toLowerCase().includes("/maps") ||
        url.pathname.toLowerCase().includes("/@"))
    ) {
      const pathMatch = url.pathname.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (pathMatch) return { lat: parseFloat(pathMatch[1]), lng: parseFloat(pathMatch[2]) };
    }
  } catch (e) {
    // URL parsing failed
  }

  // 3. Fallback: Raw Coordinate string "-25.746,28.229"
  const fallbackMatch = urlStr.trim().match(/^(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)$/);
  if (fallbackMatch) {
    return { lat: parseFloat(fallbackMatch[1]), lng: parseFloat(fallbackMatch[2]) };
  }

  return null;
}

export interface FlexReport {
  id?: string;
  companyId?: string;
  name: string;
  reportShortName?: string;
  description?: string | null;
  reportDescription?: string | null;
  dataSource?: "bigquery" | "firestore" | string;
  includedTemplates?: string[] | null;
  reportSelectStatement?: string | null;
  reportTemplateLink?: string;
  isActive?: boolean | null;
  renderingType?: string;
  moduleId?: string | null;
  moduleName?: string | null;
  moduleMetadata?: any;
  runtimeFilters?: RuntimeFilterDefinition[];
}

// ─── System Observability & Error Reporting ─────────────────────────────────

export type SystemErrorSeverity = "ERROR" | "WARNING" | "INFO";

export type SystemErrorStatus = "OPEN" | "ACKNOWLEDGED" | "RESOLVED" | "MUTED";

export type SystemErrorTimePeriod =
  | "PERIOD_1_HOUR"
  | "PERIOD_6_HOURS"
  | "PERIOD_1_DAY"
  | "PERIOD_1_WEEK"
  | "PERIOD_30_DAYS";

export interface SystemErrorGroup {
  groupId: string;
  name: string;
  message: string;
  service: string;
  version?: string;
  count: number;
  affectedUsersCount: number;
  firstSeenTime: string;
  lastSeenTime: string;
  status: SystemErrorStatus;
  severity: SystemErrorSeverity;
  action?: string;
  companyId?: string;
  userEmail?: string;
  explanation: string;
  suggestedAction?: string;
  representative?: {
    message: string;
    stack?: string;
  };
}

export interface SystemErrorEvent {
  eventId?: string;
  eventTime: string;
  service: string;
  version?: string;
  message: string;
  stack?: string;
  user?: string;
  action?: string;
  companyId?: string;
  userEmail?: string;
  httpRequest?: {
    method?: string;
    url?: string;
    userAgent?: string;
    referrer?: string;
    responseStatusCode?: number;
    remoteIp?: string;
  };
  metadata?: Record<string, unknown>;
}

export interface SystemErrorGroupsResponse {
  errorGroups: SystemErrorGroup[];
  nextPageToken?: string;
  timeRange?: string;
}

export interface SystemErrorEventsResponse {
  events: SystemErrorEvent[];
  nextPageToken?: string;
}

// ─── Real-Time Anomaly Detection & Virtual Telemetry (3-Layer Architecture) ────

export type AnomalySeverity = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export type AnomalyCategory =
  | "CONSUMPTION_SPIKE"
  | "CAPACITY_BREACH"
  | "CAPACITY_MISMATCH"
  | "METER_DISCREPANCY"
  | "TANK_RECONCILIATION"
  | "FLEX_METRIC_SPIKE"
  | "FLEX_CUMULATIVE_SURGE"
  | "FLEX_METADATA_BREACH"
  | "VISION_VERIFICATION_FAILURE"
  | "SEMANTIC_NON_COMPLIANCE"
  | "ODOMETER_REGRESSION"
  | "TANK_LEAK_DISCREPANCY"
  | "CHEMICAL_OVERDOSE"
  | "CHECKLIST_FAILURE"
  | "METER_ROLLOVER_SURGE"
  | "METADATA_BOUND_EXCEEDED"
  | "OPERATIONAL_OUTLIER";

export type AnomalySensitivity = "CONSERVATIVE" | "BALANCED" | "SENSITIVE";

/**
 * Sensitivity thresholds governing statistical pre-signals (Layer 1 & Layer 2)
 * before handing off to the Layer 3 (Option C) Contextual Anomaly Judge:
 * - CONSERVATIVE: >= 2.5x EWMA/baseline multiplier, >= 3.5σ Z-score (fewer, high-certainty alerts)
 * - BALANCED (Default): >= 2.0x EWMA/baseline multiplier, >= 3.0σ Z-score (catches "doubled consumption")
 * - SENSITIVE: >= 1.6x EWMA/baseline multiplier, >= 2.5σ Z-score (early warning for sensitive stock/chemicals)
 */
export const ANOMALY_SENSITIVITY_THRESHOLDS: Record<
  AnomalySensitivity,
  { multiplier: number; zScore: number; label: string; description: string }
> = {
  CONSERVATIVE: {
    multiplier: 2.5,
    zScore: 3.5,
    label: "Conservative (2.5x baseline / 3.5σ)",
    description: "Flags only extreme spikes (>=2.5x normal rate or >=3.5 standard deviations).",
  },
  BALANCED: {
    multiplier: 2.0,
    zScore: 3.0,
    label: "Balanced — Recommended (2.0x baseline / 3.0σ)",
    description: "Flags doubled consumption rates (>=2.0x baseline) and strong statistical outliers (>=3.0σ).",
  },
  SENSITIVE: {
    multiplier: 1.6,
    zScore: 2.5,
    label: "Sensitive (1.6x baseline / 2.5σ)",
    description: "Early-warning detection (>=1.6x baseline or >=2.5σ) ideal for high-value chemicals or strict fuel controls.",
  },
};

/**
 * Default cooldown window (in minutes) per (companyId + entityKey + category)
 * to prevent duplicate WhatsApp/Email alert fatigue on back-to-back submissions.
 */
export const DEFAULT_ANOMALY_COOLDOWN_MINUTES = 240; // 4 hours

/**
 * Configuration stored on `flexFormTemplates`, `reportNotifications`, or `flexReportNotifications`.
 */
export interface AnomalyDetectionConfig {
  /** Master toggle for AI & statistical anomaly detection */
  enabled: boolean;
  /** Statistical sensitivity tier (default: "BALANCED" -> 2.0x multiplier / 3.0σ) */
  sensitivity?: AnomalySensitivity;
  /** Minimum severity required to trigger outbound Email/WhatsApp notifications (default: "MEDIUM") */
  minimumSeverityToNotify?: AnomalySeverity;
  /** Whether to trigger an anomaly alert when Gemini Vision autoVerified fails (default: true) */
  alertOnVisionFailure?: boolean;
  /** Cooldown window in minutes per entity + category before sending another alert (default: 240) */
  cooldownMinutes?: number;
}

/**
 * Layer 1 Rolling Telemetry Statistics embedded on `fleet/{fleetId}.telemetryStats`.
 * Uses Welford's online algorithm for running variance and EWMA (alpha = 0.25) for adaptive baseline tracking.
 */
export interface FleetTelemetryStats {
  sampleCount: number;
  /** Running arithmetic mean consumption rate (L/hr for HOURS, L/100km for KILOMETERS) */
  meanConsumptionRate: number;
  /** Welford M2 accumulator for variance = m2 / (sampleCount - 1) */
  m2ConsumptionRate: number;
  /** Exponentially Weighted Moving Average rate (alpha = 0.25) */
  ewmaConsumptionRate: number;
  /** Optional activity-conditioned baselines (e.g. Ripping vs Spraying vs Transport) */
  byActivity?: Record<
    string,
    {
      sampleCount: number;
      ewmaRate: number;
      meanRate: number;
      activityName?: string;
    }
  >;
  /** Ring buffer of last 5 valid refuel rate observations */
  recentReadings?: Array<{
    date: string;
    odometer: number;
    odometerDelta: number;
    litres: number;
    rate: number;
    sourceId: string;
    activityId?: string;
  }>;
  updatedAt: string;
}

/**
 * Layer 1 Tank Mass-Balance & Reconciliation Telemetry embedded on `tanks/{tankId}.telemetryStats`.
 */
export interface TankTelemetryStats {
  /** Running expected fuel volume (litres) based on last dip reading + bulk deliveries - issues +/- transfers */
  expectedLevelLitres?: number;
  lastReconciledReadingLitres?: number;
  lastReconciledAt?: string;
  recentDiscrepancies?: Array<{
    date: string;
    expectedLitres: number;
    actualLitres: number;
    deltaLitres: number;
    sourceId: string;
  }>;
  updatedAt?: string;
}

/**
 * Snapshot stamped onto a Fuel transaction document (`capturedRefuel`, `capturedFuelTankIssue`, etc.)
 * by Layer 1 (`firebase.asset-augmentation`) so downstream reports and the Layer 3 Judge have exact deltas.
 */
export interface FuelTelemetrySnapshot {
  previousOdometer?: number;
  previousOdometerDate?: string;
  odometerDelta?: number;
  measurementUnit?: string;
  calculatedConsumptionRate?: number;
  baselineConsumptionRate?: number;
  baselineSource?: "ACTIVITY_EWMA" | "EXPECTED_RATE" | "FLEET_EWMA" | "NONE";
  rateUnit?: "L/hr" | "L/100km";
  expectedTankLevelBefore?: number;
  expectedTankLevelAfter?: number;
  evaluatedAt: string;
}

/**
 * Structured quantitative pre-signal emitted by Layer 1 (Fuel/Tank physics) or Layer 2 (Flex virtual telemetry)
 * and passed into Layer 3 (Option C `gemini-3.8-flash` Contextual Anomaly Judge).
 */
export interface QuantitativeAnomalySignal {
  ruleId:
    | "FUEL_RATE_SPIKE"
    | "CAPACITY_OVERFLOW"
    | "METER_VOLUME_MISMATCH"
    | "TANK_DIP_DISCREPANCY"
    | "TANK_RECONCILIATION"
    | "FLEX_SINGLE_VALUE_SPIKE"
    | "FLEX_7D_CUMULATIVE_SURGE"
    | "FLEX_METADATA_BOUND_BREACH";
  metricLabel: string;
  observedValue: number;
  baselineValue?: number;
  unit?: string;
  ratioToBaseline?: number;
  zScore?: number;
  entityKey?: string;
  entityLabel?: string;
  details: string;
}

/**
 * Layer 2 Online Baseline Accumulator stored at `anomalyBaselines/{baselineId}`.
 * Tracks per-(companyId, templateId, entityKey, metricKey) Welford variance, EWMA, and 14-day rolling window events.
 */
export interface AnomalyBaselineDoc {
  id?: string;
  companyId: string;
  templateId: string;
  templateName?: string;
  entityKey: string;
  entityLabel: string;
  metricKey: string;
  metricLabel: string;
  metricMode: "GAUGE" | "CUMULATIVE_METER";
  sampleCount: number;
  mean: number;
  m2: number;
  ewma: number;
  lastRawValue?: number;
  lastTimestamp?: string;
  /** Rolling 14-day event buffer used to compare 7-day cumulative usage against prior 7-day usage */
  windowEvents: Array<{
    timestamp: string;
    value: number;
    submissionId: string;
  }>;
  updatedAt: string;
}

/**
 * Lightweight summary stamped directly onto the parent Fuel transaction or `flexFormSubmissions` document
 * for instant badge rendering in `apps/report-viewer`.
 */
export interface AnomalySummary {
  isAnomaly: boolean;
  eventId?: string;
  severity: AnomalySeverity;
  category: AnomalyCategory;
  headline: string;
  explanation?: string;
  recommendedAction?: string;
  confidence: number;
  quantitativeSignals?: QuantitativeAnomalySignal[];
  evaluatedAt: string;
}

/**
 * Stored at `anomalyEvents/{eventId}`.
 * Created when the Layer 3 (Option C) Contextual Anomaly Judge confirms an operational anomaly.
 * Triggers `onAnomalyEventCreated` in `services/firebase.reports` for Email and WhatsApp alerting.
 */
export interface AnomalyEvent {
  id?: string;
  companyId: string;
  domain: "FUEL" | "FLEX";
  sourceCollection: string;
  sourceDocumentId: string;
  templateId?: string;
  templateName?: string;
  assetId?: string;
  assetName?: string;
  /** Grouping key (e.g. `fleet:tract_1` or `Section:B2|ProdIssued.Benomyl`) used for cooldown deduplication */
  entityKey: string;
  entityLabel?: string;
  severity: AnomalySeverity;
  category: AnomalyCategory;
  headline: string;
  explanation: string;
  recommendedAction: string;
  confidence: number;
  quantitativeSignals: QuantitativeAnomalySignal[];
  visionFailed?: boolean;
  storyText?: string;
  submitterName?: string;
  status: "OPEN" | "ACKNOWLEDGED" | "DISMISSED";
  notifiedChannels?: {
    email?: boolean;
    whatsapp?: boolean;
    notifiedAt?: string;
    suppressedByCooldown?: boolean;
  };
  createdAt: string;
  updatedAt?: string;
  resolvedAt?: string;
  resolutionReason?: string;
}

// ─── Customer Onboarding Support Requests & AI Onboarding Logs ────────────────

export interface OnboardingSupportNote {
  id: string;
  text: string;
  authorEmail: string;
  createdAt: string; // ISO-8601
}

export type OnboardingSupportStatus = "open" | "completed";

export type OnboardingSupportSource =
  | "ai_onboarding_wizard"
  | "registration_flow"
  | "dashboard_help"
  | "whatsapp_onboarding";

export interface OnboardingSupportRequest {
  id: string;
  companyId?: string | null;
  companyName?: string | null;
  customerUid?: string | null;
  customerName?: string | null;
  customerEmail?: string | null;
  customerPhone?: string | null;
  source: OnboardingSupportSource;
  customerNote: string;
  conversationId?: string | null;
  draftId?: string | null;
  status: OnboardingSupportStatus;
  supportNotes: OnboardingSupportNote[];
  createdAt: string; // ISO-8601
  updatedAt: string; // ISO-8601
  completedAt?: string | null;
  completedBy?: string | null;
}

export interface AIOnboardingLogSummary {
  id: string;
  conversationId?: string | null;
  draftId?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  companyPhone?: string | null;
  userEmail?: string | null;
  userUid?: string | null;
  moduleName?: string | null;
  domain?: string | null;
  status: string;
  currentTurnCount?: number;
  formCount?: number;
  optionSetCount?: number;
  reportCount?: number;
  createdAt: string;
  installedAt?: string | null;
}

export interface AIOnboardingConversationMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  turnNumber?: number;
  phase?: string;
  detectedDomains?: string[];
  hasAttachments?: boolean;
  attachmentNames?: string[];
  createdAt: string;
}

export interface AIOnboardingLogDetail {
  summary: AIOnboardingLogSummary;
  messages: AIOnboardingConversationMessage[];
  iterations: Array<{
    id: string;
    iterationNumber?: number;
    moduleName?: string;
    createdAt: string;
  }>;
  moduleConfig: any | null;
}

export * from "./schemas/entities";
export * from "./schemas/flex-module";
export * from "./schemas/lazy-zod";



